<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';

import { addCustomMixToCart } from '@/api/cart/customizers/mixes';
import { addCustomTrayToCart } from '@/api/cart/customizers/trays';
import AddToCartOrCustomize from '@/components/base/add-to-cart/AddToCartOrCustomize.vue';
import CheckIcon from '@/components/base/assets/CheckIcon.vue';
import StarIcon from '@/components/base/assets/StarIcon.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import ReviewsPopper from '@/components/reviews/ReviewsPopper.vue';
import { useCart } from '@/composables/useCart';
import { BYOB_PRODUCT_SKU } from '@/lib/customizers/boxes';
import {
  formatNutsAddToCartItemFromProductCard,
  sendProductAddedEvent,
} from '@/utils/analytics/productAddedEvent';
import { getCartId } from '@/utils/cart';
import type { LineItemsOrderDetails } from '@/utils/orderDetails';
import { ProductCardData } from '@/utils/productCard';

const props = defineProps<{
  lineItem: LineItemsOrderDetails;
  productData?: ProductCardData;
  showPricing: boolean;
  showReviewButton?: boolean;
  showReviewPopper?: boolean;
}>();

defineEmits<{
  'add-to-cart': [];
  review: [item: LineItemsOrderDetails];
}>();

const store = useStore();
const { addToCart, cart, setCart } = useCart(store);

const addedToCart = ref(false);

const requiresCustomization = computed(
  () => props.lineItem.custom && !props.lineItem.custom?.allIngredientsAvailable,
);
const outOfStock = computed(() => !props.productData?.inStock);
const addToCartText = computed(() => {
  if (outOfStock.value) return 'Out of stock';
  return addedToCart.value ? 'Added' : 'Add';
});

async function addItemToCart({
  skipCartAdded,
  sku,
  dynamicBundleSkus,
}: {
  skipCartAdded: boolean;
  sku: string;
  dynamicBundleSkus?: string[];
}) {
  await addToCart(
    {
      cart_sku: {
        dynamic_bundle_skus: dynamicBundleSkus,
        quantity: 1,
        sku_external_id: sku,
      },
    },
    {
      postAddToCartCallback: () => null,
      skipCartAdded,
    },
  );
}

const isAddToCartLoading = ref<boolean>(false);

async function handleAddToCart() {
  isAddToCartLoading.value = true;
  if (props.productData) {
    sendProductAddedEvent(
      formatNutsAddToCartItemFromProductCard(props.productData, {
        cartId: getCartId(cart.value),
        itemListName: 'order-history',
        markedAsGift: false,
        pageSection: 'Product List',
        quantity: 1,
      }),
    );
  }

  if (props.lineItem.custom?.type === 'CustomMix') {
    const cartResponse = await addCustomMixToCart(
      props.lineItem.sku,
      props.lineItem.custom.contents,
      { onRedirect: () => {} },
    );
    setCart(cartResponse);
  } else if (props.lineItem.custom?.type === 'CustomTray') {
    const cartResponse = await addCustomTrayToCart(
      props.lineItem.sku,
      props.lineItem.custom.contents,
    );
    setCart(cartResponse);
  } else {
    await addItemToCart({
      skipCartAdded: props.lineItem.sku === BYOB_PRODUCT_SKU,
      sku: props.lineItem.sku,
      dynamicBundleSkus: props.lineItem.custom?.contents,
    });
  }
  addedToCart.value = true;
  isAddToCartLoading.value = false;
}
</script>

<template>
  <li class="flex flex-row w-full py-3 lg:py-7 gap-x-6 grow">
    <img
      alt=""
      class="self-start object-contain w-24 mt-2 lg:mt-0"
      :src="lineItem.image_url || productData?.imageUrl"
    />
    <div class="flex flex-col w-full lg:flex-row lg:gap-x-6 gap-y-2">
      <div class="flex flex-col gap-0.5 justify-center flex-1">
        <a v-if="lineItem.url" class="text-black-link" :href="lineItem.url">
          <BaseBodyText class="font-semibold">{{ lineItem.name }}</BaseBodyText>
        </a>
        <BaseBodyText v-else class="font-semibold">{{ lineItem.name }}</BaseBodyText>
        <SmallBodyText class="text-neutral-500">
          <template v-if="lineItem.packaging">{{ lineItem.packaging }} | </template>
          {{ lineItem.quantity }} Qty
        </SmallBodyText>
        <SmallBodyText v-if="lineItem.custom?.description" class="text-neutral-500">
          {{ lineItem.custom.description }}
        </SmallBodyText>
      </div>

      <div
        v-if="showPricing"
        class="flex flex-row-reverse items-center justify-end lg:flex-col lg:items-end lg:justify-center gap-1.5 lg:gap-0"
      >
        <SmallBodyText
          v-if="lineItem.comparison_total !== lineItem.total"
          class="line-through text-neutral-500"
        >
          <span class="sr-only">Discounted from:</span>
          {{ lineItem.comparison_total }}
        </SmallBodyText>
        <BaseBodyText class="font-semibold text-nuts-lime-900">
          <span v-if="lineItem.comparison_total !== lineItem.total" class="sr-only">To:</span>
          {{ lineItem.total }}
        </BaseBodyText>
      </div>

      <div
        class="flex items-center justify-end gap-2"
        :class="{
          'mt-4': !showPricing,
          'lg:w-64': showReviewButton,
        }"
      >
        <component
          v-if="showReviewButton"
          :is="!showReviewPopper || !!lineItem.review?.message ? 'div' : ReviewsPopper"
          location="order-details"
          v-slot="slotProps"
        >
          <ThemedButton
            class="flex items-center flex-grow gap-2 px-2 sm:flex-grow-0 lg:flex-grow lg:w-40"
            data-test="write-a-review-button"
            :disabled="!!lineItem.review?.message"
            fullwidth
            theme="white"
            @click="
              $emit('review', lineItem);
              slotProps?.dismiss();
            "
            data-promo="1"
            data-promo-name="Write a review"
            data-promo-creative="Order View"
          >
            <component :is="lineItem.review?.message ? CheckIcon : StarIcon" :size="16" />
            <SmallBodyText class="whitespace-nowrap">
              {{ lineItem.review?.message ? 'Item reviewed' : ' Write a Review' }}
            </SmallBodyText>
          </ThemedButton>
        </component>
        <AddToCartOrCustomize
          class="add-to-cart-button flex flex-grow min-w-[6rem] sm:flex-grow-0 lg:flex-grow"
          :class="showReviewButton ? 'lg:w-24' : 'lg:w-32'"
          data-test="add-to-cart-button"
          :disabled="addedToCart || outOfStock"
          :isLoading="isAddToCartLoading"
          :outOfStock
          :path="props.lineItem?.url"
          :requiresCustomization
          @add-to-cart="handleAddToCart"
        >
          <template #add-to-cart-text>
            <SmallBodyText class="whitespace-nowrap">
              {{ addToCartText }}
            </SmallBodyText>
          </template>
        </AddToCartOrCustomize>
      </div>
    </div>
  </li>
</template>

<style scoped>
.text-black-link {
  @apply text-black;
}
</style>
